import { useState } from "react";
import { useTranslation } from "react-i18next";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');

  .cd-footer {
    width: 100%;
    background: #1a1a1a;
    padding: 48px 60px 40px;
    font-family: 'Montserrat', sans-serif;
    color: #fff;
    box-sizing: border-box;
  }

  .cd-footer * {
    box-sizing: border-box;
  }

  .cd-footer-inner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0;
  }

  /* --- Column 1: Brand + Social --- */
  .cd-col-brand {
    display: flex;
    flex-direction: column;
    gap: 28px;
    min-width: 160px;
  }

  .cd-logo {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.5px;
    color: #fff;
  }

  .cd-logo span {
    color: #e87722;
  }

  .cd-social-icons {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    align-items: center;
  }

  .cd-social-icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1.5px solid #444;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #aaa;
    transition: border-color 0.2s, color 0.2s;
    background: transparent;
    padding: 0;
  }

  .cd-social-icon:hover {
    border-color: #e87722;
    color: #e87722;
  }

  .cd-social-icon svg {
    width: 15px;
    height: 15px;
    fill: currentColor;
    display: block;
  }

  /* --- Column 2: Nav --- */
  .cd-col-nav {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px 36px;
    padding-top: 4px;
  }

  .cd-nav-link {
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #ccc;
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s;
    white-space: nowrap;
    background: none;
    border: none;
    padding: 0;
    font-family: inherit;
  }

  .cd-nav-link:hover {
    color: #fff;
  }

  .cd-nav-link.active {
    color: #e87722;
  }

  /* --- Column 3: Contact --- */
  .cd-col-contact {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 4px;
    min-width: 220px;
  }

  .cd-col-heading {
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #fff;
    margin-bottom: 6px;
  }

  .cd-contact-text {
    font-size: 13px;
    font-weight: 400;
    color: #bbb;
    line-height: 1.6;
    margin: 0;
  }

  /* --- Column 4: Subscribe --- */
  .cd-col-subscribe {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 4px;
    min-width: 220px;
  }

  .cd-email-row {
    display: flex;
    align-items: center;
    background: #2a2a2a;
    border: 1px solid #333;
    overflow: hidden;
    height: 40px;
  }

  .cd-email-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    padding: 0 14px;
    font-size: 12px;
    color: #aaa;
    font-family: 'Montserrat', sans-serif;
    letter-spacing: 0.3px;
  }

  .cd-email-input::placeholder {
    color: #666;
  }

  .cd-email-btn {
    width: 40px;
    height: 40px;
    background: #e87722;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.2s;
    padding: 0;
  }

  .cd-email-btn:hover {
    background: #d06818;
  }

  .cd-email-btn svg {
    width: 16px;
    height: 16px;
    fill: #fff;
    display: block;
  }

  .cd-payment-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .cd-badge {
    border: 1.5px solid #444;
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: #bbb;
    cursor: default;
    transition: border-color 0.2s;
    white-space: nowrap;
    font-family: 'Montserrat', sans-serif;
  }

  .cd-badge:hover {
    border-color: #666;
  }

  @media (max-width: 768px) {
    .cd-footer {
      padding: 40px 24px;
    }

    .cd-footer-inner {
      flex-direction: column;
      gap: 36px;
    }

    .cd-col-brand,
    .cd-col-nav,
    .cd-col-contact,
    .cd-col-subscribe {
      width: 100%;
      min-width: 0;
    }

    .cd-col-nav {
      grid-template-columns: repeat(2, 1fr);
      gap: 12px 24px;
    }
  }
`;

const IconHistory = () => (
  <svg viewBox="0 0 24 24"><path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/></svg>
);

const IconShare = () => (
  <svg viewBox="0 0 24 24"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/></svg>
);

const IconCamera = () => (
  <svg viewBox="0 0 24 24"><path d="M12 15.2A3.2 3.2 0 0 1 8.8 12 3.2 3.2 0 0 1 12 8.8 3.2 3.2 0 0 1 15.2 12 3.2 3.2 0 0 1 12 15.2M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9m3 15a5 5 0 0 1-5-5 5 5 0 0 1 5-5 5 5 0 0 1 5 5 5 5 0 0 1-5 5z"/></svg>
);

const IconPin = () => (
  <svg viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
);

const IconGlobe = () => (
  <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>
);

const IconChevron = () => (
  <svg viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
);

const paymentBadges = ["PayPal", "VISA", "MC", "Maestro", "AMEX"];
const socialIcons = [IconHistory, IconShare, IconCamera, IconPin, IconGlobe];

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");

  const navLinks = [
    { key: "Women", label: t('footer.women') },
    { key: "Men", label: t('footer.men') },
    { key: "Lookbook", label: t('footer.lookbook') },
    { key: "Sale", label: t('footer.sale') },
    { key: "Blog", label: t('footer.blog') },
    { key: "Contact", label: t('footer.contact') },
  ];

  return (
    <>
      <style>{styles}</style>
      <footer className="cd-footer">
        <div className="cd-footer-inner">

          {/* Brand + Social */}
          <div className="cd-col-brand">
            <div className="cd-logo">
              <span>N'</span>Shpejt
            </div>
            <div className="cd-social-icons">
              {socialIcons.map((Icon, i) => (
                <button key={i} className="cd-social-icon" aria-label={`social-${i}`}>
                  <Icon />
                </button>
              ))}
            </div>
          </div>

          {/* Nav */}
          <div className="cd-col-nav">
            {navLinks.map((link) => (
              <button
                key={link.key}
                className={`cd-nav-link${link.key === "Women" ? " active" : ""}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Contact */}
          <div className="cd-col-contact">
            <p className="cd-col-heading">{t('footer.contactUs')}</p>
            <p className="cd-contact-text">
              {t('footer.addressL1')}<br />
              {t('footer.addressL2')}
            </p>
            <p className="cd-contact-text">{t('footer.callUs')}</p>
            <p className="cd-contact-text">{t('footer.email')}</p>
          </div>

          {/* Subscribe */}
          <div className="cd-col-subscribe">
            <p className="cd-col-heading">{t('footer.subscribeToNews')}</p>
            <div className="cd-email-row">
              <input
                className="cd-email-input"
                type="email"
                placeholder={t('footer.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button className="cd-email-btn" aria-label="Subscribe">
                <IconChevron />
              </button>
            </div>
            <div className="cd-payment-badges">
              {paymentBadges.map((badge) => (
                <div key={badge} className="cd-badge">{badge}</div>
              ))}
            </div>
          </div>

        </div>
      </footer>
    </>
  );
}
